import { ENV, Environment } from '@backoffice-frontend/environment'
import {
  FccWaypointHubReturnReason,
  SdsMode,
} from '@backoffice-frontend/graphql'
import type { AdVehicleStreamHubReturnVehicleFragment } from './graphql/HubReturnVehicle.hook'

// Hub return is rolled out per fleet, and fleet ids differ per environment, so
// the allow list is keyed by environment.
const eligibleFleets: Record<Environment, string[]> = {
  [Environment.test]: ['2c99a5aa-ae34-4fa2-8b6a-0e06fcf49daf'], // Hamburg Simulation Line Service
  [Environment.local]: [],
  [Environment.development]: ['2c99a5aa-ae34-4fa2-8b6a-0e06fcf49daf'], // Hamburg Simulation Line Service
  [Environment.integration]: [
    '21ff3309-892d-42ef-a205-defbd39fe164', // Hamburg Simulation Line Service
    '1a75583d-15ba-44c8-8069-c3dac8b5a8d9', // LN Line Service (with CSC & Passthrough)
  ],
  [Environment.production]: ['1a75583d-15ba-44c8-8069-c3dac8b5a8d9'], // LN Line Service (with CSC & Passthrough)
  [Environment['production.us-east-1']]: [],
}

const isFleetEligibleForHubReturn = (vehicle: HubReturnVehicle): boolean =>
  Boolean(vehicle.fleet && eligibleFleets[ENV].includes(vehicle.fleet.id))

type HubReturnVehicle = AdVehicleStreamHubReturnVehicleFragment

const getUpcomingActions = (vehicle: HubReturnVehicle) =>
  vehicle.fccUpcomingWaypoints?.flatMap(waypoint => waypoint.actions) ?? []

const hasActiveTrips = (vehicle: HubReturnVehicle) =>
  getUpcomingActions(vehicle).some(
    action =>
      (action.__typename === 'FccWaypointPickupAction' ||
        action.__typename === 'FccWaypointDeliveryAction') &&
      Boolean(action.trip?.id),
  )

const hasExistingHubReturn = (vehicle: HubReturnVehicle) =>
  getUpcomingActions(vehicle).some(
    action =>
      action.__typename === 'FccWaypointHubReturnAction' &&
      action.reason === FccWaypointHubReturnReason.OnDemand &&
      new Date(action.estimatedAt).getTime() > Date.now(),
  )

type HubReturnDisabledReason =
  | 'noPermission'
  | 'sdsNotEngaged'
  | 'notResponsive'
  | 'poolingEnabled'
  | 'hasActiveTrips'
  | 'hasExistingHubReturn'
  | 'fleetNotEligibleForHubReturn'

const getHubReturnDisabledReason = (
  vehicle: HubReturnVehicle,
  hasPermission: boolean,
): HubReturnDisabledReason | undefined => {
  if (!hasPermission) return 'noPermission'
  if (!isFleetEligibleForHubReturn(vehicle))
    return 'fleetNotEligibleForHubReturn'
  // sdsMode is nested under latestADTelemetry (ADTelemetry.sdsMode.sdsMode)
  if (vehicle.latestADTelemetry?.sdsMode?.sdsMode !== SdsMode.Engaged) {
    return 'sdsNotEngaged'
  }
  if (vehicle.isResponsive === false) return 'notResponsive'
  const poolingEnabled = vehicle.pooling?.enabled === true
  const activeTrips = hasActiveTrips(vehicle)
  if (poolingEnabled) return 'poolingEnabled'
  if (activeTrips) return 'hasActiveTrips'
  if (hasExistingHubReturn(vehicle)) return 'hasExistingHubReturn'

  return undefined
}

export const isHubReturnEligible = (
  vehicle: HubReturnVehicle,
  hasPermission: boolean,
): boolean => getHubReturnDisabledReason(vehicle, hasPermission) === undefined

export const getHubReturnDisabledTooltip = (
  vehicle: HubReturnVehicle,
  hasPermission: boolean,
  t: (key: string) => string,
): string | undefined => {
  const reason = getHubReturnDisabledReason(vehicle, hasPermission)
  switch (reason) {
    case 'noPermission':
      return t('common_noPermission_tooltip')
    case 'poolingEnabled':
    case 'hasActiveTrips':
      return t('adFleetMap_tooltip_hubReturnDisabledGeneric_caption')
    case 'hasExistingHubReturn':
      return t('adFleetMap_tooltip_hubReturnDisabledAlreadyRequested_caption')
    case 'fleetNotEligibleForHubReturn':
      return t('adFleetMap_tooltip_hubReturnDisabledFleetNotEligible_caption')
    case 'sdsNotEngaged':
      return t('adFleetMap_tooltip_hubReturnDisabledSdsNotEngaged_caption')
    case 'notResponsive':
      return t('adFleetMap_tooltip_hubReturnDisabledNotResponsive_caption')
    default:
      return undefined
  }
}
